'use client';

/**
 * PageTabs — query-param-driven tab strip + content switcher.
 *
 * Convergence primitive after the 2026-05-18 sidebar dedup
 * (see docs/nav-dedup-2026-05-18.md). The cleanup left ~22 destination
 * pages expected to render different sub-views per `?param=…`. This
 * component centralises that pattern.
 *
 * Behaviour:
 *   - Reads the URL search-param named `param` (default 'view').
 *   - Resolves to the matching tab; falls back to `defaultKey`
 *     (or the first tab if `defaultKey` is omitted).
 *   - Tab clicks call `router.replace` with the param updated and ALL
 *     other params preserved, so deep-linked filters survive a tab
 *     switch. `replace` (not `push`) keeps the browser-history clean —
 *     hitting Back goes to where the user came from, not through every
 *     tab they sampled.
 *   - Keyboard: WAI-ARIA tabs (roving tabindex; ArrowLeft/Right, Home,
 *     End move focus and select, skipping disabled tabs). Each tab is
 *     `aria-controls` the panel, which is `aria-labelledby` the tab.
 *   - The tab strip is sticky-friendly and uses the existing slate /
 *     brand-primary tokens; pages can override via `stripClassName`.
 *
 * Usage:
 *   <PageTabs
 *     param="view"
 *     defaultKey="all"
 *     tabs={[
 *       { key: 'all',      label: 'All',      render: () => <AllList/> },
 *       { key: 'unfiled',  label: 'Unfiled',  render: () => <UnfiledList/>, badge: 12 },
 *       { key: 'archived', label: 'Archived', render: () => <Archived/> },
 *     ]}
 *   />
 */
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type KeyboardEvent, type ReactNode, useCallback, useId, useMemo, useRef } from 'react';

import { cn } from './cn';
import { nextTabIndex } from './tabsKeyboard';

export interface PageTab<K extends string = string> {
  key: K;
  label: ReactNode;
  /** Optional small count or status badge rendered after the label. */
  badge?: ReactNode;
  /** Optional icon component (e.g. lucide). Rendered before the label. */
  icon?: (props: { className?: string; size?: number }) => ReactNode;
  /** Hide the tab from the strip (still resolvable via direct URL). */
  hidden?: boolean;
  /** Disable click + show as muted. */
  disabled?: boolean;
  /** Render the tab's content. Called only for the active tab. */
  render: () => ReactNode;
}

export interface PageTabsProps<K extends string = string> {
  /** URL search-param to read/write. Defaults to 'view'. */
  param?: string;
  /** Which tab to fall back to when the URL has no/unknown value. */
  defaultKey?: K;
  tabs: readonly PageTab<K>[];
  /** Tailwind classes appended to the tab-strip <nav>. */
  stripClassName?: string;
  /** Tailwind classes appended to the content wrapper. */
  contentClassName?: string;
  /** When true, omit the param entirely for the default tab's URL.
   *  Keeps the bare path canonical for the most common entry. */
  omitParamForDefault?: boolean;
  /** Force the active tab regardless of the URL. Use for legacy URL
   *  aliases (e.g. a page wants `?view=citations|pinned|…` to all
   *  highlight the same "Knowledge Base" tab). When set, the strip
   *  highlight follows this key but the URL itself is untouched. */
  activeKeyOverride?: K;
}

export function PageTabs<K extends string = string>({
  param = 'view',
  defaultKey,
  tabs,
  stripClassName,
  contentClassName,
  omitParamForDefault = false,
  activeKeyOverride,
}: PageTabsProps<K>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const visibleTabs = useMemo(() => tabs.filter((t) => !t.hidden), [tabs]);
  const fallbackKey = (defaultKey ?? (tabs[0]?.key as K)) as K;

  const raw = searchParams?.get(param) ?? null;
  const matched = tabs.find((t) => t.key === raw);
  const urlDerivedKey = (matched?.key ?? fallbackKey) as K;
  const activeKey = (activeKeyOverride ?? urlDerivedKey) as K;
  const activeTab = tabs.find((t) => t.key === activeKey);

  const hrefFor = useCallback(
    (key: K): string => {
      const next = new URLSearchParams(searchParams?.toString() ?? '');
      if (omitParamForDefault && key === fallbackKey) {
        next.delete(param);
      } else {
        next.set(param, key);
      }
      const qs = next.toString();
      return qs ? `${pathname}?${qs}` : (pathname ?? '/');
    },
    [pathname, searchParams, param, fallbackKey, omitParamForDefault],
  );

  const onSelect = useCallback(
    (key: K) => {
      if (key === activeKey) return;
      router.replace(hrefFor(key), { scroll: false });
    },
    [router, activeKey, hrefFor],
  );

  const baseId = useId();
  const tabId = (key: string) => `${baseId}-tab-${key}`;
  const panelId = `${baseId}-panel`;
  const listRef = useRef<HTMLElement | null>(null);
  // Roving tabindex: the active tab is the one Tab stop; when the active key
  // is hidden, the first enabled visible tab takes it.
  const focusKey =
    visibleTabs.find((t) => t.key === activeKey && !t.disabled)?.key ??
    visibleTabs.find((t) => !t.disabled)?.key;

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const current = visibleTabs.findIndex((t) => t.key === (focusKey ?? activeKey));
    const next = nextTabIndex(
      e.key,
      current < 0 ? 0 : current,
      visibleTabs.map((t) => Boolean(t.disabled)),
    );
    if (next === null) return;
    e.preventDefault();
    const target = visibleTabs[next];
    if (!target) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-tab-key="${CSS.escape(String(target.key))}"]`)
      ?.focus();
    onSelect(target.key as K);
  };

  return (
    <div className="space-y-4">
      <nav
        ref={listRef}
        aria-label="Page sections"
        aria-orientation="horizontal"
        className={cn(
          'flex flex-wrap items-center gap-1 border-b border-slate-200',
          stripClassName,
        )}
        role="tablist"
        onKeyDown={onKeyDown}
      >
        {visibleTabs.map((t) => {
          const active = t.key === activeKey;
          const Icon = t.icon;
          return (
            <Link
              key={t.key}
              aria-controls={panelId}
              aria-disabled={t.disabled || undefined}
              aria-selected={active}
              className={cn(
                'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                '-mb-px', // overlap parent border so active underline is flush
                active
                  ? 'border-brand-primary-600 font-medium text-brand-primary-700'
                  : 'border-transparent text-slate-600 hover:text-slate-900',
                t.disabled && 'cursor-not-allowed opacity-60 hover:text-slate-600',
              )}
              data-tab-key={t.key}
              href={hrefFor(t.key as K)}
              id={tabId(t.key)}
              role="tab"
              tabIndex={t.key === focusKey ? 0 : -1}
              onClick={(e) => {
                if (t.disabled) {
                  e.preventDefault();
                  return;
                }
                // Let Link handle the navigation but use replace semantics.
                e.preventDefault();
                if (!t.disabled) onSelect(t.key as K);
              }}
            >
              {Icon ? <Icon className="shrink-0" size={14} /> : null}
              <span>{t.label}</span>
              {t.badge != null ? (
                <span
                  className={cn(
                    'ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                    active
                      ? 'bg-brand-primary-100 text-brand-primary-700'
                      : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {t.badge}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>
      <div
        aria-labelledby={activeTab ? tabId(activeTab.key) : undefined}
        className={contentClassName}
        id={panelId}
        role="tabpanel"
        tabIndex={0}
      >
        {activeTab ? activeTab.render() : null}
      </div>
    </div>
  );
}

/**
 * usePageTabActive — same query-param resolution as PageTabs, without the
 * UI. Useful when the page wants to render the strip in a custom layout
 * slot (e.g. inside a sticky header) but still needs to know the active key.
 *
 * Returns: { active, setActive(key) }. `setActive` calls router.replace.
 */
export function usePageTabActive<K extends string>(
  options: {
    param?: string;
    keys: readonly K[];
    defaultKey?: K;
    omitParamForDefault?: boolean;
  },
): { active: K; setActive: (k: K) => void; hrefFor: (k: K) => string } {
  const { param = 'view', keys, defaultKey, omitParamForDefault = false } = options;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const fallback = (defaultKey ?? keys[0]) as K;
  const raw = searchParams?.get(param) ?? null;
  const active = (keys.includes(raw as K) ? (raw as K) : fallback) as K;

  const hrefFor = useCallback(
    (k: K): string => {
      const next = new URLSearchParams(searchParams?.toString() ?? '');
      if (omitParamForDefault && k === fallback) next.delete(param);
      else next.set(param, k);
      const qs = next.toString();
      return qs ? `${pathname}?${qs}` : (pathname ?? '/');
    },
    [pathname, searchParams, param, fallback, omitParamForDefault],
  );

  const setActive = useCallback(
    (k: K) => {
      if (k === active) return;
      router.replace(hrefFor(k), { scroll: false });
    },
    [router, active, hrefFor],
  );

  return { active, setActive, hrefFor };
}
